import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Calculator, CalendarDays, FileText,
  Landmark, Smartphone, Download, Apple, Gavel, Upload, WalletCards,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { loadEditionStats } from "@/lib/appStats";
import { PRODUCT_CATALOG, isAppAvailable } from "@/lib/catalog";
import { PAYMENT_METHODS, PAYMENT_METHOD_KEYS } from "@/lib/payments";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const HIDDEN_CATALOG_APPS_KEY = "weborder.hiddenCatalogApps";
function getHiddenCatalogAppIds() {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_CATALOG_APPS_KEY) || "[]");
  } catch {
    return [];
  }
}

const PAYMENT_LABEL_LIST = PAYMENT_METHOD_KEYS.map((k) => PAYMENT_METHODS[k].label).join(" သို့မဟုတ် ");

const FEATURES = [
  { icon: Calculator, title: "အခွန်တွက်စက်များ", desc: "Salary, Monthly Salary, Capital Gains, Corporation, Commercial, Withholding နှင့် Association Tax များတွက်ချက်နိုင်သည်" },
  { icon: FileText,   title: "အခွန်ဥပဒေ၊ နှုန်းနှင့် ဇယားများ", desc: "အခွန်ဥပဒေများ၊ အခွန်နှုန်းထားများ၊ tax tables နှင့် exemption/relief အချက်အလက်များကို စုစည်းဖတ်ရှုနိုင်သည်" },
  { icon: CalendarDays,title: "အရေးကြီးရက်စွဲနှင့် အခွန်ပြက္ခဒိန်", desc: "PIT, PAYE, Corporation Tax, Commercial Tax, WHT, SSB စသည့် due date များကို ကြည့်နိုင်သည်" },
  { icon: Gavel,      title: "ဒဏ်ကြေးနှင့် လိုက်နာရန်အချက်များ", desc: "Late filing, underpayment, record keeping, stamp duty နှင့် compliance checklist များကို လေ့လာနိုင်သည်" },
  { icon: Landmark,   title: "CBM ငွေလဲနှုန်း", desc: "Currency exchange converter ဖြင့် အခွန်တွက်ရာတွင် လိုအပ်သော ငွေလဲနှုန်းကို အထောက်အကူပြုနိုင်သည်" },
  { icon: ShieldCheck,title: "Premium ဖြင့် လမ်းညွှန်အပြည့်အစုံ", desc: "အခွန်လမ်းညွှန်၊ ဝင်ငွေရလမ်းအမျိုးအစားများ၊ taxpayer types နှင့် advanced calculators များကို unlock လုပ်နိုင်သည်" },
];

const PLATFORM_DETAILS = {
  android: {
    label: "Android",
    sub: "Android ဖုန်း / Tablet အတွက်",
    icon: Smartphone,
    tryPath: "/try/android",
  },
  ios: {
    label: "iOS",
    sub: "iPhone / iPad အတွက်",
    icon: Apple,
    tryPath: "/try/ios",
  },
};

const BUY_STEPS = [
  { icon: Download, title: "Free version စမ်းပါ", desc: "Android ဗားရှင်းကို download ဆွဲပြီး Free version ကို အရင်စမ်းသုံးနိုင်သည်။" },
  { icon: WalletCards, title: "Premium ဝယ်ရန် ဆုံးဖြတ်ပါ", desc: `Full features လိုအပ်ပါက Premium ကို ${PAYMENT_LABEL_LIST} ဖြင့် ဝယ်ယူနိုင်သည်။` },
  { icon: Upload, title: "Screenshot တင်ပါ", desc: "ငွေလွှဲပြီးပါက payment screenshot ကို checkout တွင် upload လုပ်ပါ။" },
  { icon: ShieldCheck, title: "Premium unlock", desc: "Screenshot စစ်ဆေးအတည်ပြုပြီးနောက် Premium အသုံးပြုခွင့် ဖွင့်ပေးပါမည်။" },
];

function mergeLiveStats(apps, stats) {
  return apps.map((app) => {
    const live = stats[app.platform];
    if (!live) return app;
    return {
      ...app,
      downloads: typeof live.downloads === "number" ? live.downloads : app.downloads,
      stats_source: live.source,
    };
  });
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadApps() {
      const hiddenIds = getHiddenCatalogAppIds();
      // Only load available apps for the edition choices
      let nextApps = PRODUCT_CATALOG.filter((app) => !hiddenIds.includes(app.app_id) && isAppAvailable(app));

      try {
        const liveStats = await loadEditionStats();
        nextApps = mergeLiveStats(nextApps, liveStats);
      } catch {
        // Keep backend/fallback app data if optional live stats are unavailable.
      }

      if (!cancelled) setApps(nextApps);
    }

    loadApps();
    return () => { cancelled = true; };
  }, []);

  if (apps.length === 0) {
    return <div className="min-h-[80vh] grid place-items-center text-zinc-500">ဖွင့်နေသည်…</div>;
  }

  const android = apps.find((a) => a.platform === "android") || apps[0];
  // Only available apps make it into editionChoices (iOS is filtered out since available=false)
  const editionChoices = [
    { variant: "android", app: android },
  ].filter((item) => item.app);
  // Show iOS "Coming Soon" card if iOS exists in catalog but isn't available yet
  const showIosSoon = PRODUCT_CATALOG.some((a) => a.platform === "ios" && !isAppAvailable(a));

  const handleBuy = (appId) => {
    if (!user) { toast.info("ဆက်လုပ်ရန် ဝင်ရောက်ပါ"); navigate("/login", { state: { from: `/checkout/${appId}` } }); return; }
    navigate(`/checkout/${appId}`);
  };

  return (
    <div className="bg-[#F7F9F6]">
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-10 lg:gap-12 items-start">
            <div className="fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F5E5] text-xs tracking-wide text-[#0F4E2A] font-semibold mb-5">
                <Calculator className="w-3.5 h-3.5" /> MyanTax Box
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold leading-[1.35] tracking-tight">
                Making Myanmar Tax
                <span className="block mt-2 text-5xl sm:text-6xl font-extrabold leading-[1.25] text-[#0F4E2A]">
                  Simple
                </span>
              </h1>
              <p className="mt-4 text-xl sm:text-2xl font-semibold leading-10 text-zinc-800">
                မြန်မာအခွန်အတွက် ရိုးရှင်းသော ကူညီမှု
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-sm text-zinc-700">
                <span className="px-3 py-1.5 rounded-md bg-zinc-100">ဝန်ထမ်း</span>
                <span className="px-3 py-1.5 rounded-md bg-zinc-100">Freelancer</span>
                <span className="px-3 py-1.5 rounded-md bg-zinc-100">အသေးစား အလတ်စား လုပ်ငန်း</span>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => document.getElementById("editions")?.scrollIntoView({ behavior: "smooth" })}
                  className="h-12 rounded-lg bg-[#0F4E2A] hover:bg-[#0A3A1F] text-white"
                >
                  Download ရွေးရန် <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById("how-to-buy")?.scrollIntoView({ behavior: "smooth" })}
                  className="h-12 rounded-lg border-zinc-300"
                >
                  ဝယ်ယူနည်း ကြည့်ရန်
                </Button>
              </div>
            </div>

            <div id="editions" className="scroll-mt-24 bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
              <div className="p-5 sm:p-6 border-b border-zinc-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-[#0F4E2A]">MyanTax Box Premium</div>
                    <h2 className="mt-1 text-2xl font-bold tracking-tight">Device အလိုက် Download ရွေးပါ</h2>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-xs text-zinc-500">Premium တစ်နှစ်စာ</div>
                    <div className="text-2xl font-extrabold tabular-nums text-[#0F4E2A]">
                      {(android?.price_mmk || 30000).toLocaleString()} ကျပ်
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-7 text-zinc-600">
                  Android ဗားရှင်းကို download ဆွဲနိုင်ပါသည်။ iOS ဗားရှင်း မကြာမီ ရနိုင်ပါမည်။
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-zinc-50 p-4">
                {editionChoices.map(({ variant, app }) => (
                  <EditionChoice
                    key={variant}
                    variant={variant}
                    app={app}
                    onTry={() => navigate(PLATFORM_DETAILS[variant].tryPath)}
                    onBuy={() => handleBuy(app.app_id)}
                  />
                ))}
                {showIosSoon && <ComingSoonEdition variant="ios" />}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-to-buy" className="max-w-6xl mx-auto px-6 py-14 scroll-mt-24">
        <div className="max-w-2xl">
          <div className="text-sm tracking-wide text-zinc-500 font-medium">Free မှ Premium သို့</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">စမ်းသုံးပြီးမှ Premium ဝယ်ယူနိုင်ပါသည်</h2>
          <p className="text-zinc-600 mt-3 leading-7">
            MyanTax Box free version ကို အရင်စမ်းကြည့်နိုင်ပါသည်။ Premium ဝယ်ယူပါက ငွေလွှဲ Screenshot ကို စစ်ဆေးပြီးနောက် full features အသုံးပြုခွင့် ဖွင့်ပေးပါသည်။
          </p>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BUY_STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="bg-white border border-zinc-200 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#E8F5E5] text-[#0F4E2A] grid place-items-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-semibold text-zinc-400 tabular-nums">0{i + 1}</div>
                </div>
                <h3 className="mt-4 font-semibold text-lg">{step.title}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-600">{step.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="max-w-2xl">
            <div className="text-sm tracking-wide text-zinc-500 font-medium">အင်္ဂါရပ်များ</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
              ဘာကြောင့် MyanTax Box ကို ရွေးချယ်သင့်သလဲ?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="fade-up border border-zinc-200 rounded-xl p-5 hover:border-zinc-300 transition-colors"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-[#E8F5E5] grid place-items-center text-[#0F4E2A]">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-lg mt-4">{f.title}</h3>
                <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
            <div className="text-sm tracking-wide text-zinc-500 font-medium">Premium ဝယ်ယူပြီးနောက်</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Admin အတည်ပြုပြီးပါက Full access ရပါမည်</h2>
            <p className="mt-3 text-zinc-600 leading-7">
              Free version ကို စမ်းသုံးပြီးနောက် Premium ဝယ်ယူလိုပါက ငွေလွှဲ Screenshot တင်ပါ။ Admin မှ အတည်ပြုပြီးပါက သင့်အကောင့်တွင် Premium full features ကို အသုံးပြုနိုင်ပါသည်။
            </p>
            <p className="mt-3 text-sm text-zinc-500 leading-7">
              မှတ်ချက်။ Website တွင် register လုပ်သော email ကို App ထဲတွင်လည်း အသုံးပြုပါ။ Premium access သည် ထို email account နှင့် ချိတ်ဆက်ထားပါသည်။
            </p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-zinc-500">ငွေပေးချေနည်း</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PAYMENT_METHOD_KEYS.map((key) => {
                const m = PAYMENT_METHODS[key];
                return (
                  <span
                    key={key}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200 bg-white text-zinc-800 text-sm font-medium"
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} /> {m.label}
                  </span>
                );
              })}
            </div>
            <Button
              onClick={() => document.getElementById("editions")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-5 w-full h-11 rounded-lg bg-[#0F4E2A] hover:bg-[#0A3A1F] text-white"
            >
              ဝယ်ယူရန် စတင်ပါ
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white mt-12">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-zinc-500">
          <div>© {new Date().getFullYear()} MyanTax Box</div>
          <div className="flex gap-6">
            <span>{PAYMENT_LABEL_LIST} ဖြင့် ပေးချေ</span>
            <Link to="/dashboard" className="hover:text-[#0F4E2A]">ဝယ်ယူမှုများ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ComingSoonEdition({ variant }) {
  const details = PLATFORM_DETAILS[variant];
  if (!details) return null;
  const Icon = details.icon;
  return (
    <div
      data-testid={`edition-${variant}-soon`}
      className="flex h-full flex-col rounded-xl border border-dashed border-zinc-300 bg-zinc-50/60 p-4"
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-zinc-100 grid place-items-center text-zinc-400 flex-shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-lg text-zinc-500">{details.label}</span>
            <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold bg-zinc-200 px-2 py-0.5 rounded">မကြာမီ</span>
          </div>
          <div className="text-xs leading-5 text-zinc-500">{details.sub}</div>
        </div>
      </div>
      <div className="mt-5 text-sm leading-6 text-zinc-600">
        iOS ဗားရှင်း မကြာမီ ရနိုင်ပါမည်။ အခုအခါတွင် Android ဗားရှင်းကိုသာ ရွေးချယ်အသုံးပြုနိုင်ပါသည်။
      </div>
      <div className="mt-auto pt-4">
        <Button
          data-testid={`edition-${variant}-notify`}
          variant="outline"
          disabled
          className="h-11 w-full rounded-lg border-zinc-300 text-zinc-400 cursor-not-allowed"
        >
          မကြာမီ ရနိုင်ပါမည်
        </Button>
      </div>
    </div>
  );
}

function EditionChoice({ variant, app, onTry, onBuy }) {
  if (!app) return null;
  const details = PLATFORM_DETAILS[variant];
  const Icon = details.icon;
  const discount = app.original_price_mmk && app.original_price_mmk > app.price_mmk
    ? Math.round((1 - app.price_mmk / app.original_price_mmk) * 100)
    : 0;
  const downloadCount = Number(app.downloads || 0);

  return (
    <div
      data-testid={`edition-${variant}`}
      className="flex h-full flex-col rounded-xl border border-zinc-200 bg-white p-4 transition-all hover:border-[#0F4E2A]/40 hover:shadow-sm"
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#E8F5E5] grid place-items-center text-[#0F4E2A] flex-shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-lg">{details.label}</div>
          <div className="text-xs leading-5 text-zinc-500">{details.sub}</div>
          {downloadCount > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-zinc-500">
              <Download className="w-3 h-3" />
              <span className="tabular-nums">{downloadCount.toLocaleString()}</span>
              <span>downloads</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-baseline gap-2">
          {app.original_price_mmk && app.original_price_mmk > app.price_mmk && (
            <span className="text-sm text-zinc-400 line-through tabular-nums">{app.original_price_mmk.toLocaleString()}</span>
          )}
          <span className="text-2xl font-extrabold tabular-nums text-[#0F4E2A]">{app.price_mmk.toLocaleString()}</span>
          <span className="text-sm text-zinc-500">ကျပ်</span>
        </div>
        {discount > 0 && (
          <span className="mt-1 inline-flex items-center bg-[#0F4E2A] text-[#B5DC57] rounded-md px-2 py-0.5 text-xs font-semibold tracking-wide">
            −{discount}%
          </span>
        )}
      </div>

      <div className="mt-auto pt-4 grid grid-cols-1 gap-2">
        <Button
          data-testid={`edition-${variant}-try`}
          variant="outline"
          onClick={onTry}
          className="h-11 rounded-lg border-[#0F4E2A]/30 text-[#0F4E2A] hover:bg-[#E8F5E5]"
        >
          အခမဲ့ဗားရှင်း
        </Button>
        <Button
          data-testid={`edition-${variant}-buy`}
          onClick={onBuy}
          className="h-11 rounded-lg bg-[#0F4E2A] hover:bg-[#0A3A1F] text-white"
        >
          ဝယ်ယူရန် <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}